import { useMutation } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { DatasetDetailDto } from '@/Domain/api/Simulation/dto/Simulation.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';

type Props = {
  datasetId: number;
  metadata: Record<string, unknown>;
};

const formatMetadata = (metadata: Record<string, unknown>): string => {
  if (!metadata || Object.keys(metadata).length === 0) {
    return '';
  }
  return JSON.stringify(metadata, null, 2);
};

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const ConversationMetadataEditor = ({ datasetId, metadata }: Props) => {
  const { t } = useTranslation('simulation_page');
  const [open, setOpen] = useState(false);

  const initial = formatMetadata(metadata);
  const [draft, setDraft] = useState(initial);
  const [lastSaved, setLastSaved] = useState(initial);
  const [jsonError, setJsonError] = useState(false);

  // Sync draft with server state when the metadata prop changes and the user has no unsaved edits.
  useEffect(() => {
    const next = formatMetadata(metadata);
    setDraft((currentDraft) => {
      if (currentDraft === lastSaved) {
        setLastSaved(next);
        return next;
      }
      return currentDraft;
    });
  }, [metadata, lastSaved]);

  const updateMutation = useMutation<
    DatasetDetailDto,
    unknown,
    { datasetId: number; conversation_metadata: Record<string, unknown> }
  >({
    mutationKey: [QueryKey.simulation.updateDataset],
  });

  const isDirty = draft !== lastSaved;

  const validateJson = (value: string): boolean => {
    if (!value.trim()) return true;
    try {
      const parsed = JSON.parse(value);
      return isPlainObject(parsed);
    } catch {
      return false;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setDraft(value);
    setJsonError(!validateJson(value));
  };

  const handleBlur = () => {
    if (!jsonError && draft.trim()) {
      try {
        const parsed = JSON.parse(draft);
        if (isPlainObject(parsed)) {
          setDraft(JSON.stringify(parsed, null, 2));
        }
      } catch {
        /* validateJson already covered this branch */
      }
    }
  };

  const handleSave = () => {
    const parsed: Record<string, unknown> = draft.trim() ? JSON.parse(draft) : {};
    updateMutation.mutate(
      { datasetId, conversation_metadata: parsed },
      {
        onSuccess: () => {
          const formatted = formatMetadata(parsed);
          setLastSaved(formatted);
          setDraft(formatted);
          toast.success(t('toast_metadata_saved'));
        },
        onError: () => toast.error(t('toast_metadata_save_error')),
      },
    );
  };

  return (
    <div className="rounded-md border" data-testid="conversation-metadata-editor">
      <button
        type="button"
        data-testid="conversation-metadata-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full cursor-pointer items-center justify-between gap-2 px-3 py-2 text-left text-sm font-medium"
      >
        <span className="flex items-center gap-1.5">
          {open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          )}
          {t('conversation_metadata_label')}
        </span>
        <span className="text-xs font-normal text-muted-foreground">
          {isDirty ? t('conversation_metadata_unsaved') : t('conversation_metadata_hint')}
        </span>
      </button>
      {open && (
        <div className="space-y-2 border-t px-3 pb-3 pt-2">
          <textarea
            data-testid="conversation-metadata-textarea"
            className={`h-56 w-full resize-y rounded-md border bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring ${
              jsonError ? 'border-destructive focus:ring-destructive/30' : ''
            }`}
            value={draft}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder='{"modelName": "X95L", "locale": "en"}'
          />
          {jsonError && (
            <p className="text-xs text-destructive" data-testid="conversation-metadata-error">
              {t('conversation_metadata_invalid_json')}
            </p>
          )}
          <p className="text-xs text-muted-foreground">{t('conversation_metadata_tip')}</p>
          <div className="flex justify-end">
            <Button
              data-testid="conversation-metadata-save"
              size="sm"
              onClick={handleSave}
              disabled={!isDirty || updateMutation.isPending || jsonError}
            >
              {updateMutation.isPending ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Save className="mr-1.5 h-3.5 w-3.5" />
              )}
              {t('save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
